
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusCircle, Trash2 } from "lucide-react";
import { useUsersManagement } from "./user-management/useUsersManagement";
import { useUserDeletion } from "@/hooks/useUserDeletion";
import CreateUserForm from "./user-management/CreateUserForm";
import UsersList from "./user-management/UsersList";
import AddUserDialog from "./user-management/AddUserDialog";
import EditUserDialog from "./user-management/EditUserDialog";
import DeleteUserDialog from "./user-management/DeleteUserDialog";
import { useAuth } from "@/context/AuthContext";

const UsersTab = () => {
  const { 
    users,
    isAddDialogOpen,
    setIsAddDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    selectedUser,
    newUserEmail,
    setNewUserEmail,
    newUserName,
    setNewUserName,
    newUserRole,
    setNewUserRole,
    isSubmitting,
    handleAddUser,
    handleEditUser,
    saveUserChanges,
    refreshUsers
  } = useUsersManagement();
  
  const {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    selectedUserForDeletion,
    openDeleteDialog,
    handleDeleteUser
  } = useUserDeletion();

  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  
  const [isCreateUserFormVisible, setIsCreateUserFormVisible] = useState(false);

  const handleUserCreationSuccess = () => {
    setIsCreateUserFormVisible(false);
  };

  const handleDeleteConfirm = async (password: string, notes: string) => {
    const success = await handleDeleteUser(password, notes);
    if (success) {
      // Refresh the users list after deletion
      refreshUsers();
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Usuários da Conta</CardTitle>
        <CardDescription>
          Gerencie os usuários que têm acesso ao sistema
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex justify-between">
            <Button 
              className="bg-brand-teal hover:bg-brand-teal/90"
              onClick={() => setIsCreateUserFormVisible(true)}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Adicionar Usuário
            </Button>
          </div>
          
          {isCreateUserFormVisible && (
            <Card className="mt-4 border-brand-teal/20 shadow-sm">
              <CardHeader className="bg-brand-teal/5 py-3">
                <CardTitle className="text-lg">Criar Novo Usuário</CardTitle>
                <CardDescription>
                  Adicione um novo usuário com acesso ao sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <CreateUserForm 
                  onSuccess={handleUserCreationSuccess}
                  onCancel={() => setIsCreateUserFormVisible(false)}
                />
              </CardContent>
            </Card>
          )}
          
          <UsersList 
            users={users} 
            onEditUser={handleEditUser}
            onDeleteUser={isAdmin ? openDeleteDialog : undefined}
          />
        </div>
      </CardContent>
      
      {/* Add User Dialog */}
      <AddUserDialog
        isOpen={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onAddUser={handleAddUser}
        newUserEmail={newUserEmail}
        setNewUserEmail={setNewUserEmail}
        newUserName={newUserName}
        setNewUserName={setNewUserName}
        newUserRole={newUserRole}
        setNewUserRole={setNewUserRole}
        isSubmitting={isSubmitting}
      />
      
      {/* Edit User Dialog */}
      {selectedUser && (
        <EditUserDialog
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSaveChanges={saveUserChanges}
          userName={newUserName}
          setUserName={setNewUserName}
          userEmail={selectedUser.email}
          userRole={newUserRole}
          setUserRole={setNewUserRole}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete User Dialog */}
      <DeleteUserDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirmDelete={handleDeleteConfirm}
        user={selectedUserForDeletion}
      />
    </Card>
  );
};

export default UsersTab;
